/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-08
 */

// Add third-party dependencies.
import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
} from '@mui/icons-material';

// Add local dependencies.
import { useChangePasswordMutation } from '../../features/auth';
import { useFormValidation } from '../../hooks/useFormValidation';
import { changePasswordSchema } from '../../utils/validationSchemas';
import { sanitizeErrorMessage } from '../../utils/security';
import {
  validatePasswordStrength,
  getPasswordStrengthScore,
  getPasswordStrengthLabel,
  getPasswordStrengthColor,
} from '../../utils/passwordStrength';
import { accessibilityUtils, ARIA_ROLES } from '../../utils/accessibility';

/**
 * Form data interface for password change.
 */
interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

/**
 * Props for the ChangePasswordForm component.
 */
interface ChangePasswordFormProps {
  /**
   * Callback function called when the password is successfully changed.
   */
  onSuccess?: () => void;
  /**
   * Callback function called when the user cancels the operation.
   */
  onCancel?: () => void;
}

/**
 * ChangePasswordForm component that allows users to change their password.
 * Includes current password verification, new password validation with strength indicator,
 * and comprehensive error handling.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The change password form component
 *
 * @example
 * return (
 *   <ChangePasswordForm
 *     onSuccess={() => setChangePasswordMode(false)}
 *     onCancel={() => setChangePasswordMode(false)}
 *   />
 * );
 */
const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [formData, setFormData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    currentPassword: 'idle' | 'valid' | 'invalid';
    newPassword: 'idle' | 'valid' | 'invalid';
    confirmNewPassword: 'idle' | 'valid' | 'invalid';
  }>({
    currentPassword: 'idle',
    newPassword: 'idle',
    confirmNewPassword: 'idle',
  });
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize validation hook
  const {
    errors,
    validateForm,
    handleInputChange: handleValidationChange,
  } = useFormValidation<PasswordFormData>(changePasswordSchema, {
    validateOnBlur: true,
  });

  /**
   * Handles input changes for form fields.
   * @param e - The input change event
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Use validation hook's input change handler
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    handleValidationChange(
      name as keyof PasswordFormData,
      value,
      updatedFormData
    );

    // Update field validation states for visual feedback
    const fieldName = name as keyof PasswordFormData;
    const hasError = errors[fieldName];
    const hasValue = value.trim().length > 0;

    let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
    if (hasError) {
      validationState = 'invalid';
    } else if (hasValue) {
      validationState = 'valid';
    }

    setFieldValidationStates(prev => ({
      ...prev,
      [fieldName]: validationState,
    }));

    // Clear messages when user starts typing
    if (apiError) setApiError('');
    if (successMessage) setSuccessMessage('');
  };

  /**
   * Handles form submission.
   * @param e - The form submit event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await changePassword({
        old_password: formData.currentPassword,
        new_password: formData.newPassword,
        new_password_confirm: formData.confirmNewPassword,
      }).unwrap();

      setSuccessMessage(result.message || 'Password changed successfully!');

      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      setFieldValidationStates({
        currentPassword: 'idle',
        newPassword: 'idle',
        confirmNewPassword: 'idle',
      });

      // Call onSuccess callback after a brief delay to show success message
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000); // Slightly longer to show success state
      }
    } catch (error: any) {
      setApiError(sanitizeErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: keyof PasswordFormData) => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  /**
   * Handles form cancellation.
   */
  const handleCancel = () => {
    // Reset form
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setApiError('');
    setSuccessMessage('');
    setFieldValidationStates({
      currentPassword: 'idle',
      newPassword: 'idle',
      confirmNewPassword: 'idle',
    });

    if (onCancel) {
      onCancel();
    }
  };

  // Generate unique IDs for accessibility
  const formId = accessibilityUtils.generateId('change-password-form');
  const currentPasswordErrorId = accessibilityUtils.generateId(
    'current-password-error'
  );
  const newPasswordErrorId =
    accessibilityUtils.generateId('new-password-error');
  const confirmPasswordErrorId = accessibilityUtils.generateId(
    'confirm-password-error'
  );
  const passwordStrengthId = accessibilityUtils.generateId('password-strength');

  return (
    <Paper sx={{ p: 3, position: 'relative' }} role={ARIA_ROLES.FORM}>
      <Typography variant="h6" gutterBottom id={`${formId}-title`}>
        Change Password
      </Typography>

      {/* Loading Progress Bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: '4px 4px 0 0',
          }}
        />
      )}

      {/* Success Message */}
      <Fade in={!!successMessage}>
        <Box>
          {successMessage && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
              icon={<CheckCircle />}
            >
              {successMessage}
            </Alert>
          )}
        </Box>
      </Fade>

      {/* Error Message */}
      <Fade in={!!apiError}>
        <Box>
          {apiError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {apiError}
            </Alert>
          )}
        </Box>
      </Fade>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: 1,
        }}
        open={isSubmitting}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Changing password...
          </Typography>
        </Box>
      </Backdrop>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Current Password Field */}
        <TextField
          fullWidth
          label="Current Password"
          name="currentPassword"
          type={showCurrentPassword ? 'text' : 'password'}
          value={formData.currentPassword}
          onChange={handleInputChange}
          error={!!errors.currentPassword}
          helperText={errors.currentPassword}
          disabled={isLoading || isSubmitting}
          required
          aria-invalid={!!errors.currentPassword}
          aria-describedby={
            errors.currentPassword ? currentPasswordErrorId : undefined
          }
          FormHelperTextProps={{
            id: currentPasswordErrorId,
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              },
            },
            '& .MuiInputLabel-root': {
              transition: 'all 0.2s ease-in-out',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('currentPassword')}
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  sx={{
                    ml: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* New Password Field */}
        <TextField
          fullWidth
          label="New Password"
          name="newPassword"
          type={showNewPassword ? 'text' : 'password'}
          value={formData.newPassword}
          onChange={handleInputChange}
          error={!!errors.newPassword}
          helperText={errors.newPassword}
          disabled={isLoading || isSubmitting}
          required
          aria-invalid={!!errors.newPassword}
          aria-describedby={
            errors.newPassword ? newPasswordErrorId : passwordStrengthId
          }
          FormHelperTextProps={{
            id: newPasswordErrorId,
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              },
            },
            '& .MuiInputLabel-root': {
              transition: 'all 0.2s ease-in-out',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('newPassword')}
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  sx={{
                    ml: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Password Strength Indicator */}
        {formData.newPassword && (
          <Box sx={{ mt: 1, mb: 2 }} id={passwordStrengthId}>
            <Typography variant="caption" color="text.secondary">
              Password strength:{' '}
              {getPasswordStrengthLabel(
                getPasswordStrengthScore(formData.newPassword)
              )}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 4,
                backgroundColor: '#e0e0e0',
                borderRadius: 2,
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  width: `${(getPasswordStrengthScore(formData.newPassword) / 4) * 100}%`,
                  height: '100%',
                  backgroundColor: getPasswordStrengthColor(
                    getPasswordStrengthScore(formData.newPassword)
                  ),
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
            {validatePasswordStrength(formData.newPassword).length > 0 && (
              <Box sx={{ mt: 1 }}>
                {validatePasswordStrength(formData.newPassword).map(error => (
                  <Typography
                    key={error}
                    variant="caption"
                    color="error"
                    display="block"
                  >
                    • {error}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Confirm New Password Field */}
        <TextField
          fullWidth
          label="Confirm New Password"
          name="confirmNewPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmNewPassword}
          onChange={handleInputChange}
          error={!!errors.confirmNewPassword}
          helperText={errors.confirmNewPassword}
          disabled={isLoading || isSubmitting}
          required
          aria-invalid={!!errors.confirmNewPassword}
          aria-describedby={
            errors.confirmNewPassword ? confirmPasswordErrorId : undefined
          }
          FormHelperTextProps={{
            id: confirmPasswordErrorId,
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              },
            },
            '& .MuiInputLabel-root': {
              transition: 'all 0.2s ease-in-out',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('confirmNewPassword')}
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  sx={{
                    ml: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'scale(1.1)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={handleCancel}
            disabled={isLoading || isSubmitting}
            sx={{
              minWidth: 120,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderWidth: '2px',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
                borderColor: 'action.disabled',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={
              isLoading || isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save />
              )
            }
            disabled={isLoading || isSubmitting}
            sx={{
              minWidth: 160,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
                transform: 'none',
                boxShadow: 'none',
              },
            }}
          >
            {isLoading || isSubmitting ? 'Changing...' : 'Change Password'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ChangePasswordForm;
